import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { LucideIcon } from 'lucide-react'
import type { Tab } from '../../hooks/multiWindow'
import { useActiveTab, useTabActions } from '../../contexts/TabManagerContext'
import { DormantIcon } from '../../components/icons/DormantIcon'
import { UpcomingIcon } from '../../components/icons/UpcomingIcon'
import { DEFAULT_TAB_CONFIGS } from '../../hooks/multiWindow/useTabManager'
import {
  RecurringPage,
  ProjectPage,
} from '../../features/taskManagement'
import { GoalsView } from '../../features/goals'
import { HabitsView } from '../../features/habits'
import { QuickNotesPage } from '../../features/taskManagement/pages/QuickNotesPage'
import { UpcomingPage } from '../../features/taskManagement/pages/UpcomingPage'
import { DormantPage } from '../../features/taskManagement/pages/DormantPage'
import { DonePage } from '../../features/taskManagement/pages/DonePage'

// Module-level regex constants — avoids recompiling on every render. Each
// pattern is anchored so partial paths don't match accidentally.
const LIFE_SPACE = '(personal|work|home)'
// Legacy "Im Blick" path. Tabs still sitting on `/tasks` after the
// Bereit/Im-Blick refactor are redirected to `/dormant` by
// LegacyTasksRedirect below.
const LEGACY_TASKS_RE = new RegExp(`^/home/${LIFE_SPACE}/tasks$`)
const LEGACY_TODAY_RE = new RegExp(`^/home/${LIFE_SPACE}/today$`)
const DORMANT_RE = new RegExp(`^/home/${LIFE_SPACE}/dormant$`)
const DONE_RE = new RegExp(`^/home/${LIFE_SPACE}/done$`)
const QUICK_NOTES_RE = new RegExp(`^/home/${LIFE_SPACE}/quick-notes$`)
const UPCOMING_RE = new RegExp(`^/home/${LIFE_SPACE}/upcoming$`)
const GOALS_RE = new RegExp(`^/home/${LIFE_SPACE}/goals$`)
const HABITS_RE = new RegExp(`^/home/${LIFE_SPACE}/habits$`)
const RECURRING_RE = new RegExp(`^/home/${LIFE_SPACE}/recurring$`)
const PROJECT_RE = new RegExp(`^/home/${LIFE_SPACE}/project/(.+)$`)
// Retired `/area/…` paths. The only one ever opened was the fixed "Erste Schritte in
// HappyTask" page (removed in #208); tabs from older sessions land on the default tab.
const LEGACY_AREA_RE = new RegExp(`^/home/${LIFE_SPACE}/area/.+$`)
// Titles the legacy `/tasks` tab might have carried before the refactor.
// Matching titles get renamed to "Ruhend" on redirect; custom-renamed titles
// are left untouched.
const LEGACY_TASKS_AUTO_RENAME_TITLES = new Set([
  'Im Blick',
  'In Focus',
  'Bereit',
  'Ready',
])

// Titles the retired per-space "Heute" tab might have carried. Matching titles
// get renamed to "Demnächst" on redirect; custom-renamed titles are left alone.
const LEGACY_TODAY_AUTO_RENAME_TITLES = new Set([
  'Heute',
  'Today',
  'Mein Tag',
  'My Day',
])

type PageLifeSpace = 'personal' | 'work' | 'home'

interface LegacyTasksRedirectProps {
  tab: Tab
  lifeSpace: PageLifeSpace
}

/**
 * One-shot redirect for tabs still pointing at the legacy `/tasks` path.
 * Rewrites the tab's path to `/<lifeSpace>/dormant`, auto-renames the title
 * if it matches one of the known pre-refactor labels (Im Blick / Bereit /
 * English equivalents), and syncs the URL when this tab is the active one.
 * After the tab state flips, the next render of the parent TabContent
 * matches DORMANT_RE and mounts DormantPage.
 */
const LegacyTasksRedirect: React.FC<LegacyTasksRedirectProps> = ({ tab, lifeSpace }) => {
  const { activeTabId } = useActiveTab()
  const { updateTab } = useTabActions()
  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    const newPath = `/home/${lifeSpace}/dormant`
    const updates: Partial<Tab> = { path: newPath }
    if (LEGACY_TASKS_AUTO_RENAME_TITLES.has(tab.title)) {
      updates.title = t('sidebar.dormant')
      updates.icon = DormantIcon as LucideIcon
    }
    updateTab(tab.id, updates)
    if (tab.id === activeTabId) {
      navigate(newPath, { replace: true })
    }
    // One-shot on mount: intentionally no deps beyond the tab id, so a tab
    // that somehow re-enters this render path doesn't loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab.id])

  return null
}

/**
 * One-shot redirect for tabs still pointing at the retired per-space
 * `/today` path. Those pages held hardcoded demo content and were reachable
 * only via the default tab — with no matching sidebar entry, so nothing was
 * ever selected on the left. Rewrites the tab to `/<lifeSpace>/upcoming`,
 * the closest surviving view ("Demnächst"), and syncs the URL when this tab
 * is the active one.
 */
const LegacyTodayRedirect: React.FC<LegacyTasksRedirectProps> = ({ tab, lifeSpace }) => {
  const { activeTabId } = useActiveTab()
  const { updateTab } = useTabActions()
  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    const newPath = `/home/${lifeSpace}/upcoming`
    const updates: Partial<Tab> = { path: newPath }
    if (LEGACY_TODAY_AUTO_RENAME_TITLES.has(tab.title)) {
      updates.title = t('sidebar.upcoming')
      updates.icon = UpcomingIcon
    }
    updateTab(tab.id, updates)
    if (tab.id === activeTabId) {
      navigate(newPath, { replace: true })
    }
    // One-shot on mount, same reasoning as LegacyTasksRedirect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab.id])

  return null
}

/**
 * **Ein Tab ohne Ziel bekommt den Standard-Tab seines Bereichs.**
 *
 * Bis 2026-09-12 stand hier der Platzhalter „Übersicht" — vierzehn Zeilen mit
 * festem Titel — und zwar für `/home`, `/home/overview` und jeden unbekannten
 * Pfad. Keiner dieser Wege war Absicht; jeder war ein Symptom (ein Tab aus
 * einer alten Sitzung, ein Bereich, dessen letzter Tab weggeräumt wurde).
 * Jetzt passiert dasselbe wie bei den alten `/tasks`- und `/today`-Pfaden:
 * einmaliger Umbau auf den Standard-Tab, kein Platzhalter.
 *
 * Ist der Standard-Pfad DER Pfad, der ersetzt werden soll — der Bereich
 * „Alle" zeigt auf `/home/overview` — bleibt der Tab still, sonst liefe der
 * Umbau im Kreis.
 */
const DefaultTabRedirect: React.FC<{ tab: Tab; lifeSpace: string }> = ({ tab, lifeSpace }) => {
  const { activeTabId } = useActiveTab()
  const { updateTab } = useTabActions()
  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    const config = DEFAULT_TAB_CONFIGS[lifeSpace] ?? DEFAULT_TAB_CONFIGS.personal
    if (!config || config.path === tab.path) return
    updateTab(tab.id, { path: config.path, title: t(config.titleKey), icon: config.icon as LucideIcon })
    if (tab.id === activeTabId) {
      navigate(config.path, { replace: true })
    }
    // One-shot on mount, same reasoning as LegacyTasksRedirect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab.id])

  return null
}

// Runtime type-guard — the Tab interface types `lifeSpace` as plain `string`
// (covers values like `'all'` that some pages don't accept), so we validate
// before forwarding to lifespace-scoped page components.
const isPageLifeSpace = (value: unknown): value is PageLifeSpace =>
  value === 'personal' || value === 'work' || value === 'home'

interface TabContentProps {
  tab: Tab
}

/**
 * Resolves `tab.path` to the matching page component. Mirrors the route
 * table that used to live in `HomeLayout`, but as a plain function so every
 * tab can be rendered in parallel (outside the `<Routes>` tree).
 *
 * Page components that need to know whether their tab is currently visible
 * (`QuickNotesPage`, `UpcomingPage`, `ProjectPage`, etc.) call
 * `useIsActiveTab()` from `ActiveTabContext` — the previous `isActive`
 * prop would trigger a full cross-tab re-render cascade on every tab
 * switch (TabContent couldn't memo-bail on isActive flips).
 */
export const TabContent: React.FC<TabContentProps> = React.memo(({ tab }) => {
  const { path } = tab
  const lifeSpace: PageLifeSpace = isPageLifeSpace(tab.lifeSpace) ? tab.lifeSpace : 'personal'

  // Path matchers — ordered from most specific to least specific.

  // `/home` und `/home/overview` sind keine Seiten mehr — siehe DefaultTabRedirect.
  if (path === '/home/overview' || path === '/home') {
    return <DefaultTabRedirect tab={tab} lifeSpace={tab.lifeSpace || 'personal'} />
  }

  // Legacy "Im Blick" path — redirect to Ruhend (Dormant).
  if (LEGACY_TASKS_RE.test(path)) return <LegacyTasksRedirect tab={tab} lifeSpace={lifeSpace} />

  // Retired per-space "Heute" path — redirect to Demnächst (Upcoming).
  if (LEGACY_TODAY_RE.test(path)) return <LegacyTodayRedirect tab={tab} lifeSpace={lifeSpace} />

  // Ruhend (Dormant) — paused tasks
  if (DORMANT_RE.test(path)) return <DormantPage lifeSpace={lifeSpace} />

  // Geschafft (Done) — completed tasks, by day
  if (DONE_RE.test(path)) return <DonePage lifeSpace={lifeSpace} />

  // Quick Notes
  if (QUICK_NOTES_RE.test(path)) return <QuickNotesPage lifeSpace={lifeSpace} />

  // Upcoming
  if (UPCOMING_RE.test(path)) return <UpcomingPage lifeSpace={lifeSpace} />

  // Goals (Ziele) — all three life spaces
  if (GOALS_RE.test(path)) return <GoalsView lifeSpace={lifeSpace} />

  // Habits (Gewohnheiten) — all three life spaces
  if (HABITS_RE.test(path)) return <HabitsView lifeSpace={lifeSpace} />

  // Wiederkehrend — all three life spaces
  if (RECURRING_RE.test(path)) return <RecurringPage lifeSpace={lifeSpace} />

  // Legacy `/area/…` path — known, so rebuilt without the unknown-path warning.
  if (LEGACY_AREA_RE.test(path)) return <DefaultTabRedirect tab={tab} lifeSpace={lifeSpace} />

  // Project pages — projectId is embedded in the path; extract and pass as
  // prop so we don't depend on `useParams` (which only fires inside a
  // matched Route).
  const projectMatch = path.match(PROJECT_RE)
  if (projectMatch) {
    return <ProjectPage lifeSpace={lifeSpace} projectId={projectMatch[2]} tab={tab} />
  }

  // Unknown path — warn in dev and rebuild the tab onto its space's default.
  if (process.env.NODE_ENV !== 'production') {
    console.warn('[TabContent] Unknown tab path, rebuilding onto the default tab:', path)
  }
  return <DefaultTabRedirect tab={tab} lifeSpace={lifeSpace} />
})

TabContent.displayName = 'TabContent'
